// Recommended vs listed, and "did this answer name any company at all".
//
// Pure, dependency-free. A copy lives at src/lib/brand-placement.ts so the app
// can classify older rows the same way; keep the two files identical.

export type Placement = "recommended" | "listed" | "unclear";

/** Accounts that get the v2 visibility rules (branded / informational / placement). */
export const V2_FLAG_DOMAINS = ["monroya.ai", "brandyunruh.com", "rarityfinder.com"];

export function hostOf(url: string | null | undefined): string {
  if (!url) return "";
  try {
    return new URL(url.startsWith("http") ? url : `https://${url}`).hostname.replace(/^www\./, "").toLowerCase();
  } catch {
    return "";
  }
}

export function isV2Domain(url: string | null | undefined): boolean {
  const h = hostOf(url);
  return !!h && V2_FLAG_DOMAINS.includes(h);
}

function esc(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Normalize rendered or markdown-wrapped copy before matching brand names. */
function matchableText(value: string): string {
  return value
    .toLowerCase()
    .replace(/https?:\/\//g, "")
    .replace(/^www\./g, "")
    .replace(/[*_~`#[\](){}<>|]/g, " ")
    .replace(/[^a-z0-9.]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function textNamesBrand(text: string, names: string[]): boolean {
  const haystack = ` ${matchableText(text)} `;
  return names.some((name) => {
    const needle = matchableText(name);
    return needle.length >= 3 && haystack.includes(` ${needle} `);
  });
}

const RECOMMEND_RE =
  /\b(top pick|my pick|first choice|best (choice|option|bet|pick|fit)|(i|we)('d| would)? (recommend|suggest|go with)|(i|we) recommend|highly recommend(ed)?|stands out as (the )?(best|top)|my recommendation|number one choice|#1 choice|go-to choice|clear (winner|choice)|the one (i|we)('d| would) pick)\b/i;

const LIST_LINE_RE = /^\s*(\d+[.)]|[-*•])\s+/;

/**
 * Rule pass. "recommended" only when a preference phrase sits in the same
 * sentence or list item as the brand. "listed" when the brand is one of
 * several items with no preference. Anything else is "unclear" (sent to the
 * short AI check).
 */
export function classifyPlacement(text: string, names: string[]): Placement {
  const body = (text || "").replace(/\r/g, "");
  const clean = names.map((n) => n.trim()).filter((n) => n.length >= 3);
  if (!body || !clean.length) return "unclear";
  const nameRe = new RegExp(`\\b(${clean.map(esc).join("|")})\\b`, "i");
  if (!nameRe.test(body)) return "unclear";

  const lines = body.split("\n");
  const listLines = lines.filter((l) => LIST_LINE_RE.test(l));
  const brandListIdx = listLines.findIndex((l) => nameRe.test(l));

  // Same list item or same sentence as a preference phrase.
  const units = [
    ...listLines,
    ...body.split(/(?<=[.!?])\s+|\n+/),
  ];
  for (const u of units) {
    if (nameRe.test(u) && RECOMMEND_RE.test(u)) return "recommended";
  }

  if (brandListIdx >= 0 && listLines.length >= 3) {
    // First item of a ranked list is ambiguous: could be a pick, could be order.
    if (brandListIdx === 0) return "unclear";
    return "listed";
  }
  return "unclear";
}

const COMPANY_SUFFIX_RE =
  /\b[A-Z][\w&'-]+(?:\s+[A-Z][\w&'-]+){0,3}\s+(Inc\.?|LLC|Ltd\.?|Corp\.?|Co\.|Company|Group|Realty|Real Estate|Agency|Partners|Associates|Solutions|Software|Technologies|Labs|Studio|Homes|Properties|Brokerage|Team)\b/;
const DOMAIN_RE = /\b[a-z0-9-]+\.(com|io|ai|co|net|org|app|dev)\b/i;

/**
 * Conservative: true when the answer plausibly names any business. We only
 * call a question "informational" after three scans in a row where this is
 * false for every answer, so false positives here just keep it normal.
 */
export function answerNamesACompany(
  text: string,
  mentionedEntities: unknown,
  citedSources: unknown,
): boolean {
  if (Array.isArray(mentionedEntities) && mentionedEntities.some((m: any) => (m?.mention_count ?? 0) > 0)) return true;
  if (
    Array.isArray(citedSources) &&
    citedSources.some((c: any) => ["competitor", "owned", "competitor_site", "own_site"].includes(c?.source_type))
  ) return true;
  const t = text || "";
  return COMPANY_SUFFIX_RE.test(t) || DOMAIN_RE.test(t);
}

/** True when a question contains the brand name (or a bare brand domain label). */
export function isBrandedPrompt(promptText: string, brandNames: string[]): boolean {
  return textNamesBrand(promptText, brandNames);
}

/** Brand names to match: company name plus domain labels ("brandyunruh"). */
export function brandNamesFor(name: string | null | undefined, website: string | null | undefined, domains: string[] = []): string[] {
  const out = new Set<string>();
  if (name) {
    const full = name.trim();
    out.add(full);
    const short = full.replace(/\s+(real estate|realty|properties|brokerage|team|group)$/i, "").trim();
    if (short.length >= 3) out.add(short);
  }
  for (const d of [website, ...domains]) {
    const h = hostOf(d);
    if (h) {
      out.add(h);
      out.add(h.split(".")[0]);
    }
  }
  return [...out].filter((n) => n.length >= 3);
}

const NAME_RE = new RegExp(COMPANY_SUFFIX_RE.source, "g");
const LEAD_WORDS = /^(?:(?:The|A|An|Best|Top|Your|Our|Their|This|That|Local|Colorado|Denver|Boulder|Erie|Longmont|Lafayette|Broomfield|Consider|Contact|Try|Look|Check|Choose|Hire|Ask|Many|Some|Most|Other|Any|For|With|And|Or|If|In|At|Why|How|What|When|Switching|Switch|CO|High-Review|Highly|Rated|Top-Rated|Market|International)\s+)+/;
const GENERIC = /^(Real Estate|Realty|Group|Team|Company|Agency|Homes|Properties|Brokerage|Partners|Associates|Solutions|Software|Technologies|Labs|Studio|Inc\.?|LLC|Ltd\.?|Corp\.?|Co\.)$/i;

/**
 * Business names in an answer that aren't one of `known` (tracked names).
 * Uses the same company-suffix pattern as answerNamesACompany, then trims
 * lead-in words ("The", "Best", town names) so "Best Erie Realty" → "Realty" is dropped.
 */
export function untrackedBusinessNames(text: string, known: string[]): string[] {
  const out = new Map<string, string>();
  const plain = (text || "").replace(/\*\*|__/g, "");
  const knownLc = known.map((k) => k.toLowerCase()).filter((k) => k.length >= 3);
  for (const m of plain.matchAll(NAME_RE)) {
    const name = m[0].replace(LEAD_WORDS, "").trim();
    if (!name || GENERIC.test(name) || !/\s/.test(name)) continue;
    const lc = name.toLowerCase();
    if (knownLc.some((k) => lc.includes(k) || k.includes(lc))) continue;
    if (!out.has(lc)) out.set(lc, name);
  }
  return Array.from(out.values());
}
